<script lang="ts" setup>
    import { computed } from "vue";
    /**
     * Summary values for each category.
     */
    interface SummaryValues {
        planerad_medelvarde?: number | null;
        genomford_medelvarde?: number | null;
        differens?: number | null;
    }
    /**
     * Data structure for the summary table.
     */
    interface SummaryTableData {
        forberedelse: SummaryValues;
        operationstid: SummaryValues;
        avveckling: SummaryValues;
        total_tid_forberedelse_och_avveckling: SummaryValues;
    }
    /**
     * Define props
     */
    const props = defineProps<{
        summaryTableData: SummaryTableData;
        totalOperations?: number;
        totalOperationTime?: number;
        isLoadingGenomfordData?: boolean;
    }>();
    /**
     * Computed for "Förberedelse".
     */
    const forberedelse = computed(() => props.summaryTableData.forberedelse);
    /**
     * Computed for "Operationstid".
     */
    const operationstid = computed(() => props.summaryTableData.operationstid);
    /**
     * Computed for "Avveckling".
     */
    const avveckling = computed(() => props.summaryTableData.avveckling);
    /**
     * Computed for "Total tid förberedelse och avveckling".
     */
    const total_tid_forberedelse_och_avveckling = computed(
        () => props.summaryTableData.total_tid_forberedelse_och_avveckling
    );
    /**
     * Round a number to zero decimal places.
     */
    function roundToZeroDecimal(num?: number | null): number | string {
        return typeof num === "number" ? Math.round(num) : "";
    }
    /**
     * Format differens value.
     */
    function formatDifferens(value?: number | null): string {
        const formattedValue = roundToZeroDecimal(value);

        if (typeof formattedValue !== "number") {
            return formattedValue;
        }

        if (formattedValue < 0) {
            return formattedValue.toString();
        } else if (formattedValue > 0) {
            return `+${formattedValue}`;
        } else {
            return "0";
        }
    }
</script>

<template>
    <div class="operations-summary">
        <table>
            <tbody>
                <tr class="body-header-row heading-style lightline-border-block">
                    <th colspan="1" class="padding">Total antal operationer</th>
                    <th colspan="1" class="padding align-left">
                        <p>{{ totalOperations }}</p>
                    </th>
                    <th colspan="1" class="padding align-right" />
                </tr>
                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th colspan="1" class="padding">Total operationstid (minuter)</th>
                    <th colspan="1" class="padding align-left">
                        <p>{{ totalOperationTime }}</p>
                    </th>
                    <th colspan="1" class="padding align-right" />
                </tr>

                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th class="header-vertical-padding-8 align-middle">
                        <div class="header-text align-left">Kategori</div>
                    </th>
                    <th class="header-vertical-padding-8 align-middle">
                        <div class="header-text align-left">Planerad<br />(medelvärde, antal minuter)</div>
                    </th>
                    <th class="header-vertical-padding-8 align-middle">
                        <div class="header-text align-left">Genomförd<br />(medelvärde, antal minuter)</div>
                    </th>
                    <th class="header-vertical-padding-8 align-middle">
                        <div class="header-text align-left">Differens</div>
                    </th>
                </tr>
                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th colspan="1" class="padding">Förberedelse</th>
                    <th colspan="1" class="padding align-left">
                        <p>{{ roundToZeroDecimal(forberedelse.planerad_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ roundToZeroDecimal(forberedelse.genomford_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ formatDifferens(forberedelse.differens) }}</p>
                    </th>
                </tr>

                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th colspan="1" class="padding">Operationstid</th>
                    <th colspan="1" class="padding align-left">
                        <p>{{ roundToZeroDecimal(operationstid.planerad_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ roundToZeroDecimal(operationstid.genomford_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ formatDifferens(operationstid.differens) }}</p>
                    </th>
                </tr>

                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th colspan="1" class="padding">Avveckling</th>
                    <th colspan="1" class="padding align-left">
                        <p>{{ roundToZeroDecimal(avveckling.planerad_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ roundToZeroDecimal(avveckling.genomford_medelvarde) }}</p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else>{{ formatDifferens(avveckling.differens) }}</p>
                    </th>
                </tr>

                <tr class="body-header-row heading-style lightline-border-bottom">
                    <th colspan="1" class="padding">Total tid, förberedelse och avveckling</th>
                    <th colspan="1" class="padding align-left">
                        <p class="total">
                            {{ roundToZeroDecimal(total_tid_forberedelse_och_avveckling.planerad_medelvarde) }}
                        </p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else class="total">
                            {{ roundToZeroDecimal(total_tid_forberedelse_och_avveckling.genomford_medelvarde) }}
                        </p>
                    </th>

                    <th colspan="1" class="padding align-left">
                        <span v-if="isLoadingGenomfordData" class="skeleton" />
                        <p v-else class="total">
                            {{ formatDifferens(total_tid_forberedelse_och_avveckling.differens) }}
                        </p>
                    </th>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables.scss";

    .operations-summary {
        table {
            border-collapse: collapse;
            width: 1100px;
            margin: 25px 0;

            thead {
                color: $color-table-header;
                font-family: Roboto, Medium;
                font-weight: 500;
                font-size: 14px;
            }
        }

        .header-text {
            white-space: pre-wrap;
        }

        .header-vertical-padding-8 {
            padding: 8px 0;
        }

        .padding {
            padding: 15px 0px;
        }

        .align-right {
            text-align: right;
        }

        .align-middle {
            text-align: center;
        }

        .align-left {
            text-align: left;
        }

        .lightline-border-bottom {
            border-bottom: 1px solid $divider-color;
        }

        .lightline-border-block {
            border-block: 1px solid $divider-color;
        }

        .total {
            font-family: Roboto Medium, sans-serif;
        }

        .skeleton {
            display: inline-block;
            width: 48px;
            height: 14px;
            border-radius: 4px;
            background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
            background-size: 200% 100%;
            animation: skeleton-shimmer 1.4s infinite;
            vertical-align: middle;
        }

        @keyframes skeleton-shimmer {
            0% {
                background-position: 200% 0;
            }
            100% {
                background-position: -200% 0;
            }
        }

        tbody {
            .heading-style {
                color: $color-table-header;
                font-family: Roboto Medium, sans-serif;
                font-weight: 400;
                font-style: normal;
                font-size: 14px;
                height: 1rem;
            }
            p {
                font-family: Roboto, sans-serif;
                line-height: 1rem;
                display: inline-block;
                vertical-align: middle;
                color: $color-text-black;
                font-size: 14px;
            }
        }
    }
</style>
